import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Leaf, Calendar, Clock, Car, ArrowLeft,
  ChevronRight, LogOut, Plus, Loader2, CheckCircle, 
  Clock as ClockIcon, XCircle, Gift, Users, Copy, Check, Wallet,
  X, Edit, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';import axios from 'axios';
import { format, parseISO, isPast, differenceInHours } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `/api`;

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader } = useAuth();
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showWalletDetail, setShowWalletDetail] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bookingsRes, walletRes] = await Promise.all([
          axios.get(`${API}/bookings`, { headers: getAuthHeader() }),
          axios.get(`${API}/user/wallet`, { headers: getAuthHeader() })
        ]);
        setBookings(bookingsRes.data);
        setWallet(walletRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAuthHeader]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const copyReferralCode = () => {
    if (wallet?.referral_code) {
      navigator.clipboard.writeText(wallet.referral_code);
      setCopied(true);
      toast.success('Code copié !');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const upcomingBookings = bookings.filter(b => !isPast(parseISO(b.pickup_datetime)));
  const pastBookings = bookings.filter(b => isPast(parseISO(b.pickup_datetime)));

  const getStatusBadge = (booking) => {
    if (booking.status === 'confirmed' || booking.payment_status === 'paid') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
          <CheckCircle className="w-3 h-3" />
          Confirmé
        </span>
      );
    }
    if (booking.status === 'cancelled') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
          <XCircle className="w-3 h-3" />
          Annulé
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400">
        <ClockIcon className="w-3 h-3" />
        En attente
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="px-4 sm:px-6 py-4 sm:py-6 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigate('/client')}
              className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
              data-testid="back-client-btn"
            >
              <ArrowLeft className="w-5 h-5 text-zinc-400" />
            </button>
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
              data-testid="logo-home-btn"
            >
              <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-10 h-10 sm:w-14 sm:h-14" />
              <span className="font-bold text-base sm:text-xl font-['Outfit'] text-white">YOUMI GREEN</span>
              <span className="text-emerald-500 font-bold text-base sm:text-xl font-['Outfit']">VTC</span>
            </button>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <Button 
              className="btn-primary text-xs sm:text-sm px-2 sm:px-4"
              onClick={() => navigate('/booking')}
              data-testid="new-booking-btn"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('new_booking')}</span>
            </Button>
            <Button 
              variant="ghost" 
              className="text-zinc-400 hover:text-white text-xs sm:text-sm px-2 sm:px-4"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-6 sm:py-12">
        <div className="max-w-6xl mx-auto">
          {/* Welcome */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-xl sm:text-3xl font-bold text-white font-['Outfit'] mb-1 sm:mb-2">
              {t('dashboard_hello')}, {user?.first_name} !
            </h1>
            <p className="text-zinc-400 text-sm sm:text-base">
              {t('dashboard_subtitle')}
            </p>
          </div>

          {/* Wallet & Referral Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Cagnotte - Cliquable pour voir les détails */}
            <div 
              className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-emerald-500/30 bg-emerald-500/5 cursor-pointer hover:border-emerald-500/50 transition-colors"
              onClick={() => setShowWalletDetail(true)}
              data-testid="wallet-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-emerald-500" />
                </div>
                <div className="flex-1">
                  <p className="text-zinc-400 text-sm">Ma Cagnotte</p>
                  <p className="text-3xl font-bold text-emerald-500 font-['Outfit']">
                    {wallet?.wallet_balance?.toFixed(2) || '0.00'}€
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-500" />
              </div>
              <p className="text-zinc-500 text-xs">
                Automatiquement deduite de vos courses. Cliquez pour voir le detail.
              </p>
            </div>

            {/* Wallet Detail Modal */}
            {showWalletDetail && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowWalletDetail(false)}>
                <div className="glass-card rounded-2xl p-5 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-emerald-500" />
                      <h3 className="text-lg font-bold text-white font-['Outfit']">Detail Cagnotte</h3>
                    </div>
                    <button onClick={() => setShowWalletDetail(false)} className="text-zinc-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="text-center mb-5 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                    <p className="text-zinc-400 text-sm">Solde total</p>
                    <p className="text-4xl font-bold text-emerald-500 font-['Outfit']">
                      {wallet?.wallet_balance?.toFixed(2) || '0.00'}€
                    </p>
                  </div>

                  {/* Wallet History */}
                  <div className="space-y-2">
                    <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-2">Historique des mouvements</p>
                    {(!wallet?.wallet_history || wallet.wallet_history.length === 0) ? (
                      <p className="text-zinc-600 text-sm text-center py-4">Aucun mouvement</p>
                    ) : (
                      wallet.wallet_history.map((h, i) => (
                        <div key={h.id || i} className="flex items-start justify-between py-3 px-3 bg-zinc-800/50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className={`text-sm font-bold ${h.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {h.amount > 0 ? '+' : ''}{h.amount?.toFixed(2)}€
                              </span>
                              {h.admin_action && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">Admin</span>
                              )}
                            </div>
                            <p className="text-zinc-300 text-sm truncate">{h.reason}</p>
                          </div>
                          <span className="text-zinc-600 text-xs shrink-0 ml-2">
                            {h.created_at ? format(parseISO(h.created_at), 'dd/MM/yy', { locale: fr }) : '-'}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Code Parrainage */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <Gift className="w-6 h-6 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-zinc-400 text-sm">Mon Code Parrainage</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-white font-mono">
                      {wallet?.referral_code || '---'}
                    </span>
                    <button
                      onClick={copyReferralCode}
                      className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
                      data-testid="copy-referral-btn"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-zinc-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-zinc-500 text-sm mb-3">
                Partagez ce code avec vos amis. Vous recevez chacun <strong className="text-emerald-400">10€</strong> après leur 1ère course !
              </p>
              <div className="flex gap-4 text-center">
                <div className="flex-1 p-3 bg-zinc-800 rounded-lg">
                  <p className="text-2xl font-bold text-white">{wallet?.validated_referrals || 0}</p>
                  <p className="text-xs text-emerald-400">Validés</p>
                </div>
                <div className="flex-1 p-3 bg-zinc-800 rounded-lg">
                  <p className="text-2xl font-bold text-yellow-400">{wallet?.pending_referrals || 0}</p>
                  <p className="text-xs text-zinc-400">En cours</p>
                </div>
              </div>
            </div>
          </div>

          {/* Referrals List */}
          {wallet?.referrals?.length > 0 && (
            <div className="glass-card rounded-2xl p-6 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-purple-400" />
                <h3 className="text-lg font-bold text-white">Mes Filleuls</h3>
              </div>
              <div className="space-y-3">
                {wallet.referrals.map((referral, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-white">
                          {referral.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                        </span>
                      </div>
                      <span className="text-white text-sm">{referral.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {referral.status === 'validated' ? (
                        <>
                          <span className="text-emerald-400 font-bold">+{referral.bonus}€</span>
                          <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                            Validé
                          </span>
                        </>
                      ) : (
                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                          En cours
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-12">
            <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-6">
              <p className="text-zinc-400 text-xs sm:text-sm mb-1">Courses à venir</p>
              <p className="text-xl sm:text-3xl font-bold text-white font-['Outfit']">{upcomingBookings.length}</p>
            </div>
            <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-6">
              <p className="text-zinc-400 text-xs sm:text-sm mb-1">Effectuées</p>
              <p className="text-xl sm:text-3xl font-bold text-white font-['Outfit']">{pastBookings.length}</p>
            </div>
            <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-6">
              <p className="text-zinc-400 text-xs sm:text-sm mb-1">CO₂ évité</p>
              <p className="text-xl sm:text-3xl font-bold text-emerald-500 font-['Outfit']">
                {Math.round(bookings.reduce((acc, b) => acc + b.distance_km * 0.12, 0))} kg
              </p>
            </div>
          </div>

          {/* Bookings */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-16">
              <Car className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white font-['Outfit'] mb-2">
                {t('dashboard_no_booking')}
              </h2>
              <p className="text-zinc-400 mb-6">
                Vous n'avez pas encore effectué de réservation.
              </p>
              <Button 
                className="btn-primary"
                onClick={() => navigate('/booking')}
                data-testid="first-booking-btn"
              >
                {t('dashboard_first_booking')}
              </Button>
            </div>
          ) : (
            <Tabs defaultValue="upcoming" className="w-full">
              <TabsList className="bg-zinc-900 border border-zinc-800 p-1 rounded-xl mb-4 sm:mb-8 w-full">
                <TabsTrigger 
                  value="upcoming" 
                  className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black rounded-lg px-3 sm:px-6 text-xs sm:text-sm flex-1"
                  data-testid="upcoming-tab"
                >
                  {t('dashboard_upcoming_tab')} ({upcomingBookings.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="past"
                  className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black rounded-lg px-3 sm:px-6 text-xs sm:text-sm flex-1"
                  data-testid="past-tab"
                >
                  {t('dashboard_past_tab')} ({pastBookings.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upcoming">
                {upcomingBookings.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    Aucune course à venir
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upcomingBookings.map((booking) => (
                      <BookingCard 
                        key={booking.id} 
                        booking={booking} 
                        getStatusBadge={getStatusBadge}
                        onRefresh={async () => {
                          const [bookingsRes, walletRes] = await Promise.all([
                            axios.get(`${API}/bookings`, { headers: getAuthHeader() }),
                            axios.get(`${API}/user/wallet`, { headers: getAuthHeader() })
                          ]);
                          setBookings(bookingsRes.data);
                          setWallet(walletRes.data);
                        }}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="past">
                {pastBookings.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    Aucune course passée
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pastBookings.map((booking) => (
                      <BookingCard key={booking.id} booking={booking} getStatusBadge={getStatusBadge} isPast />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
    </div>
  );
};

const BookingCard = ({ booking, getStatusBadge, isPast = false, onRefresh }) => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  const pickupDate = parseISO(booking.pickup_datetime);
  const hoursUntilPickup = differenceInHours(pickupDate, new Date());
  const isCancellable = booking.status !== 'cancelled' && booking.status !== 'completed' && !isPast;
  const cancellationFee = hoursUntilPickup < 1 ? 9 : 0;
  
  const handleCancel = async (e) => {
    e.stopPropagation();
    setCancelling(true);
    try {
      const response = await axios.post(
        `${API}/bookings/${booking.id}/cancel`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Réservation annulée');
      if (response.data.cancellation_fee > 0) {
        toast.info(`Frais d'annulation: ${response.data.cancellation_fee}€`);
      }
      setShowCancelModal(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <div 
        className={`glass-card rounded-2xl p-4 sm:p-6 card-hover ${isPast ? 'opacity-75' : ''}`}
        data-testid={`booking-card-${booking.id}`}
      >
        <div className="flex flex-col gap-4">
          <div 
            className="flex-1 cursor-pointer"
            onClick={() => navigate(`/booking/confirmation?booking_id=${booking.id}`)}
          >
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 flex-wrap">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                <span className="text-white font-medium text-sm sm:text-base">
                  {format(pickupDate, 'EEE d MMM yyyy', { locale: fr })}
                </span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                <span className="text-white text-sm sm:text-base">
                  {format(pickupDate, 'HH:mm')}
                </span>
              </div>
              {getStatusBadge(booking)}
            </div>

            <div className="flex gap-3">
              <div className="flex flex-col items-center pt-1">
                <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                <div className="w-0.5 h-8 bg-zinc-700" />
                <div className="w-2 h-2 bg-red-500 rounded-full" />
              </div>
              <div className="flex-1 space-y-2">
                <p className="text-xs sm:text-sm text-zinc-300 line-clamp-1">{booking.pickup_address}</p>
                <p className="text-xs sm:text-sm text-zinc-300 line-clamp-1">{booking.destination_address}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-zinc-800 pt-3">
            <div>
              <p className="text-xl sm:text-2xl font-bold text-emerald-500 font-['Outfit']">
                {booking.price?.toFixed(2) || '0.00'}€
              </p>
              <p className="text-xs text-zinc-500">
                {booking.distance_km?.toFixed(1) || '0'} km • ~{Math.round(booking.duration_min || 0)} min
              </p>
              {booking.wallet_used > 0 && (
                <p className="text-xs text-purple-400">
                  -{booking.wallet_used.toFixed(2)}€ cagnotte
                </p>
              )}
            </div>
            
            {/* Action buttons for upcoming bookings */}
            {isCancellable && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-800 text-xs px-2 sm:px-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/booking/modify/${booking.id}`);
                  }}
                  data-testid="modify-booking-btn"
                >
                  <Edit className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Modifier</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs px-2 sm:px-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCancelModal(true);
                  }}
                  data-testid="cancel-booking-btn"
                >
                  <X className="w-3.5 h-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">Annuler</span>
                </Button>
              </div>
            )}
            
            {!isCancellable && (
              <ChevronRight className="w-5 h-5 text-zinc-500" />
            )}
          </div>
        </div>
      </div>
      
      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-zinc-900 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 w-full sm:max-w-md border border-zinc-800 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Annuler la réservation ?</h3>
                <p className="text-zinc-400 text-sm">Cette action est irréversible</p>
              </div>
            </div>
            
            <div className="bg-zinc-800 rounded-lg p-4 mb-4">
              <p className="text-white mb-2">
                {format(pickupDate, 'EEEE d MMMM yyyy à HH:mm', { locale: fr })}
              </p>
              <p className="text-zinc-400 text-sm">{booking.pickup_address}</p>
            </div>
            
            {cancellationFee > 0 ? (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4">
                <p className="text-yellow-400 font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Frais d'annulation : {cancellationFee}€
                </p>
                <p className="text-zinc-400 text-sm mt-1">
                  Annulation moins d'1h avant le départ
                </p>
              </div>
            ) : (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-4">
                <p className="text-emerald-400 font-medium">
                  ✓ Annulation gratuite
                </p>
                <p className="text-zinc-400 text-sm mt-1">
                  Plus d'1h avant le départ
                </p>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-zinc-700"
                onClick={() => setShowCancelModal(false)}
              >
                Retour
              </Button>
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Confirmer l'annulation
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
